import { Collapse, Stack, Typography } from '@mui/material';
import React, { cloneElement, isValidElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowDownIcon } from '@/shared/assets/icons/arrow-down-icon';
import { ArrowUpIcon } from '@/shared/assets/icons/arrow-up-icon';
import {
  ItemToggleWrapper,
  ItemWrapper,
} from '@/shared/components/ui/form-selected-item-container/form-selected-item-container.styled';
import { TFormSelectedItemContainerProps } from '@/shared/components/ui/form-selected-item-container/form-selected-item-container.types';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';

export const FormSelectedItemContainer: React.FC<
  TFormSelectedItemContainerProps
> = ({
  isLoading,
  disabled,
  icon,
  label,
  action,
  options,
  defaultExpanded = true,
  labelMaxLength,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const _disabled = disabled || !options;

  const renderIcon = () => {
    if (!isValidElement<React.HTMLAttributes<HTMLElement>>(icon)) {
      return icon;
    }
    return cloneElement(icon, { className: 'form-selected-item-container' });
  };

  const renderName = () => {
    if (isLoading) {
      return (
        <Typography variant="p5" color="textSecondary">
          {t('common:loading')}
        </Typography>
      );
    }

    if (typeof label === 'string') {
      return (
        <TruncatedText
          text={label}
          maxLength={labelMaxLength ?? 40}
          variant="p5"
        />
      );
    }

    return label;
  };

  const handleExpand = () => {
    if (_disabled) {
      return;
    }
    setExpanded((prev) => !prev);
  };

  return (
    <ItemWrapper>
      <ItemToggleWrapper
        direction="row"
        justifyContent="space-between"
        gap="1rem"
        alignItems="center"
        onClick={handleExpand}
        $disabled={_disabled}
      >
        <Stack direction="row" gap="0.5rem" alignItems="center">
          {renderIcon()}
          {renderName()}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          marginRight="-0.25rem"
          gap="0.75rem"
        >
          {action && (
            <Stack
              direction="row"
              alignItems="center"
              gap="0.5rem"
              onClick={(event) => event.stopPropagation()}
            >
              {action}
            </Stack>
          )}
          {options &&
            (expanded ? (
              <ArrowUpIcon size="1rem" />
            ) : (
              <ArrowDownIcon size="1rem" />
            ))}
        </Stack>
      </ItemToggleWrapper>
      {options && (
        <Collapse in={expanded}>
          <Stack gap="1rem" margin="0.375rem 0 0.875rem 0">
            {options}
          </Stack>
        </Collapse>
      )}
    </ItemWrapper>
  );
};
